import { useState, useEffect, useRef } from 'react';
import { RouteComponentProps, useNavigate } from '@reach/router';
import { PolicyStreamPageContainer } from './components/PageContainer';
import { useEnsureFormPresent } from '../../hooks/useEnsureFormPresent';
import { POLICYSTREAM_FORMS } from './constants';
import { usePolicyStreamGoogleAnalytics } from '../../hooks/usePolicyStreamGoogleAnalytics';
import {
  GAEventNames,
  EventActions,
} from '../../hooks/usePolicyStreamGoogleAnalytics/types';
import Typography from '@mui/material/Typography';
import { Form, FormApi } from 'informed';
import RenderedQuestionInstance from '../common/QuoteComponents/RenderedQuestionInstance';
import { withFormStyles, WithFormStyles } from '../../util/withFormStyles';
import { styled } from '@mui/material';
import { policyStreamPaths } from './policyStreamConstants';
import { useSelector } from 'react-redux';
import { generateGetAnsweredForm } from '../../store/policystreamStore';
import { answerInstancesToFormState } from '../QuoteWizard/QuoteForms/utility';
import { usePolicyStreamSubmitAnswers, useSetStepRelevance } from '../../hooks';
import { formStateToAnswerInstances } from '../QuoteWizard/QuoteForms/utility';
import { $TSFixMe } from '@calefy-inc/utilityTypes';
import { QuoteWizardForm } from '../QuoteWizard/classes';
import { PolicyStreamLayout } from './components';
import { useScrollToTopOnInitialMount } from '@calefy-inc/hooks';
import { PolicyStreamFormCard } from './components/PolicyStreamFormCard';
import { ArrowOutward } from '@mui/icons-material';
import { PolicyStreamButton } from './components/PolicyStreamButton';
import { useSetStepStatus } from '../../hooks';
import { InitialValues } from '../../Typescript/forms';

const availablePaths = [
  policyStreamPaths.planSelection.slug,
  policyStreamPaths.recommendations.slug,
] as const;

interface PolicyStreamBaseInformationPageProps
  extends WithFormStyles,
    RouteComponentProps {}
export const PolicyStreamBaseInformationPage = withFormStyles()(
  ({ classes }: PolicyStreamBaseInformationPageProps) => {
    const myAnalytics = usePolicyStreamGoogleAnalytics();

    useScrollToTopOnInitialMount();
    const setStepStatus = useSetStepStatus('Basic Information');

    // Send Google Analytics event on component load
    useEffect(() => {
      myAnalytics?.triggerEvent(
        GAEventNames.BaseInformation,
        EventActions.PageLoad,
        'PolicyStream Base Information Page',
      );
    }, [myAnalytics]);
    const [initialValues, setInitialValues] = useState<
      InitialValues | undefined
    >();
    const basicInfo = useEnsureFormPresent({
      internalName: POLICYSTREAM_FORMS.basicInfo,
    });
    const basicInfoAnswers = useSelector(
      generateGetAnsweredForm({ id: basicInfo?.id || '' }),
    );

    // show that we're currently at this step
    useEffect(() => {
      setStepStatus('IN_PROGRESS');
    }, []);

    useEffect(() => {
      if (basicInfoAnswers) {
        const newInitialValues = basicInfoAnswers
          ? answerInstancesToFormState(
              basicInfoAnswers.answers,
              basicInfo?.questionInstances || [],
            )
          : undefined;

        setInitialValues(newInitialValues);
      }
    }, [basicInfoAnswers, basicInfo]);

    return (
      <PolicyStreamLayout>
        <PolicyStreamPageContainer>
          <Typography variant='h3' fontWeight={700}>
            Basic Information
          </Typography>
          <Typography
            marginTop={(theme) => theme.spacing(2)}
            marginBottom={(theme) => theme.spacing(2)}
          >
            We just need to collect a few basic pieces of information from you
            to get started!
          </Typography>
          <PolicyStreamFormCard>
            <BasicInfoForm
              initialValues={initialValues}
              form={basicInfo}
              classes={classes}
            />
          </PolicyStreamFormCard>
        </PolicyStreamPageContainer>
      </PolicyStreamLayout>
    );
  },
);

interface BasicInfoFormProps {
  form?: QuoteWizardForm;
  classes: $TSFixMe;
  // initialValues?: object;
  initialValues?: Record<string, unknown>;
}
const BasicInfoForm = ({
  initialValues,
  form,
  classes = {},
}: BasicInfoFormProps) => {
  const setStepStatus = useSetStepStatus('Basic Information');
  const setNeedsAnalysisStepRelevance = useSetStepRelevance('Needs Analysis');
  const formApiRef = useRef<FormApi>();
  const [destination, setDestination] = useState<typeof availablePaths[number]>(
    policyStreamPaths.planSelection.slug,
  );
  const [initialValuesSet, setInitialValuesSet] = useState<boolean>(false); // have the initial values been set? If so we don't do it again
  const navigate = useNavigate();
  const submitAnswers = usePolicyStreamSubmitAnswers();

  useEffect(() => {
    if (initialValues && formApiRef.current && !initialValuesSet) {
      formApiRef.current.setValues(initialValues);
      setInitialValuesSet(true);
    }
  }, [initialValues]);

  if (!form) {
    return null;
  }
  return (
    <Form
      initialValues={initialValues}
      autoComplete='off'
      allowEmptyStrings
      className={classes.form}
      formApiRef={formApiRef}
      onSubmit={(formState) => {
        const { values } = formState;
        if (!form) {
          return;
        }
        const answers = formStateToAnswerInstances(
          values,
          form.questionInstances,
        );
        submitAnswers({ form: form, answers });
        setStepStatus('COMPLETED');
        navigate(destination);
      }}
    >
      {(
        {
          /* formState */
        },
      ) => {
        return (
          <>
            {/*
             <pre>{JSON.stringify(initialValues, null, 4)}</pre>
            <pre>{JSON.stringify(formState, null, 4)}</pre>
              */}
            {(form?.questionInstances || []).map((questionInstance) => {
              return (
                <RenderedQuestionInstance
                  questionInstance={questionInstance}
                  classes={classes}
                />
              );
            })}
            <Typography>
              Ready to get a quote? You can go directly to see the available
              plans, or if you're unsure, we can provide you with a
              recommendation.
            </Typography>
            <ActionButtonsContainer>
              <PolicyStreamButton
                type='submit'
                disabled={!form}
                variant='outlined'
                onClick={() => {
                  setNeedsAnalysisStepRelevance(false);
                  setDestination(policyStreamPaths.planSelection.slug);
                }}
              >
                See Plans
                <ArrowOutward />
              </PolicyStreamButton>
              <PolicyStreamButton
                type='submit'
                disabled={!form}
                variant='contained'
                onClick={() => {
                  setNeedsAnalysisStepRelevance(true);
                  setDestination(policyStreamPaths.personalize.slug);
                }}
              >
                Get a Recommendation
                <ArrowOutward />
              </PolicyStreamButton>
            </ActionButtonsContainer>
          </>
        );
      }}
    </Form>
  );
};

const ActionButtonsContainer = styled('div')(({ theme }) => {
  return {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'right',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  };
});
